import type {
	ContactDecisionMakerDraftSource,
	ContactDecisionMakerReasonId,
	ContactDecisionMakerSendFailure,
} from '#src/app/shared/utils/contactDecisionMaker';
import { isContactDecisionMakerFlowOnScreen } from '#src/app/shared/utils/contactDecisionMaker';

import type { ContactDecisionMakerFlowState } from './types';

export type ContactDecisionMakerFlowAction =
	| { type: 'open' }
	| { type: 'dismiss' }
	| { type: 'pickReason'; reasonId: ContactDecisionMakerReasonId }
	| { type: 'backToReasons' }
	| { type: 'setStory'; story: string }
	| { type: 'requestDraft' }
	| { type: 'draftReady'; draft: string; source: ContactDecisionMakerDraftSource }
	| { type: 'writeOwnDraft'; seededDraft: string }
	| { type: 'editDraft' }
	| { type: 'setDraft'; draft: string }
	| { type: 'finishEditing' }
	| { type: 'send' }
	| { type: 'sendFailed'; failure: ContactDecisionMakerSendFailure }
	| { type: 'sendSucceeded' }
	| { type: 'openShare' }
	| { type: 'closeShare' }
	| { type: 'finish' };

export const INITIAL_CONTACT_DECISION_MAKER_FLOW_STATE: ContactDecisionMakerFlowState = {
	stage: 'idle',
	reasonId: undefined,
	story: '',
	draft: '',
	draftSource: undefined,
	sendFailure: undefined,
};

/**
 * Transitions come straight from the tech doc's state table (§2).
 *
 * An action that doesn't apply to the current stage is ignored rather than throwing: a double-tap or
 * a mutation resolving after the supporter already dismissed the sheet is ordinary, not a bug worth
 * crashing the page over. The stage guards are what keep those late resolutions from yanking someone
 * back into a screen they already left.
 */
// eslint-disable-next-line complexity -- one branch per transition; splitting it would only hide the state table
export function contactDecisionMakerFlowReducer(
	state: ContactDecisionMakerFlowState,
	action: ContactDecisionMakerFlowAction,
): ContactDecisionMakerFlowState {
	switch (action.type) {
		case 'open':
			// Only from a closed stage. An entry surface can still be reachable while the flow is on
			// screen, and re-entering mid-run would discard it: from `sending` it drops the send in flight
			// exactly as `dismiss` did, and from `success`/`share` it throws away the confirmation.
			if (isContactDecisionMakerFlowOnScreen(state.stage)) return state;
			// A fresh run every time, so a dismissed half-written story never reappears.
			return { ...INITIAL_CONTACT_DECISION_MAKER_FLOW_STATE, stage: 'story:pick' };

		case 'dismiss':
			// Not while the send is in flight. Preview wires ESC, the backdrop and the X to `dismiss`, and
			// resetting here would drop the `sendSucceeded` that follows: the supporter would be shown the
			// un-contacted entry state for a message that was actually delivered, and could send it twice.
			if (state.stage === 'sending') return state;
			return INITIAL_CONTACT_DECISION_MAKER_FLOW_STATE;

		case 'pickReason':
			if (state.stage !== 'story:pick') return state;
			return { ...state, stage: 'story:write', reasonId: action.reasonId };

		case 'backToReasons':
			if (state.stage !== 'story:write') return state;
			// Keeps `story`: coming back to the picker to change your mind shouldn't discard what you wrote.
			return { ...state, stage: 'story:pick' };

		case 'setStory':
			if (state.stage !== 'story:write') return state;
			return { ...state, story: action.story };

		case 'requestDraft':
			if (state.stage !== 'story:write') return state;
			return { ...state, stage: 'drafting' };

		case 'draftReady':
			if (state.stage !== 'drafting') return state;
			return { ...state, stage: 'preview', draft: action.draft, draftSource: action.source };

		case 'writeOwnDraft':
			if (state.stage !== 'story:write') return state;
			// Both story CTAs converge on the read-only review (Janice/Blair, 2026-09-01): choosing to write
			// your own words carries them into review rather than opening a separate editing screen.
			// `preview:editing` is still reachable, but only by tapping the message there — see `editDraft`.
			return { ...state, stage: 'preview', draft: action.seededDraft, draftSource: undefined };

		case 'editDraft':
			if (state.stage !== 'preview') return state;
			return { ...state, stage: 'preview:editing' };

		case 'setDraft':
			if (state.stage !== 'preview:editing') return state;
			// A rejected message is only rejected as written: once it changes, sending is offered again.
			// A blocked or unconfirmed one is not about the body at all, so editing must not re-arm a send
			// that cannot succeed, or that would deliver a second message.
			return {
				...state,
				draft: action.draft,
				sendFailure:
					state.sendFailure === 'blocked' || state.sendFailure === 'unconfirmed' ? state.sendFailure : undefined,
			};

		case 'finishEditing':
			if (state.stage !== 'preview:editing') return state;
			return { ...state, stage: 'preview' };

		case 'send':
			// Only from the read-only preview: the review step is never skippable and nothing auto-sends.
			if (state.stage !== 'preview') return state;
			return { ...state, stage: 'sending', sendFailure: undefined };

		case 'sendFailed':
			if (state.stage !== 'sending') return state;
			return { ...state, stage: 'preview', sendFailure: action.failure };

		case 'sendSucceeded':
			if (state.stage !== 'sending') return state;
			return { ...state, stage: 'success' };

		case 'openShare':
			if (state.stage !== 'success') return state;
			return { ...state, stage: 'share' };

		case 'closeShare':
			if (state.stage !== 'share') return state;
			return { ...state, stage: 'success' };

		case 'finish':
			if (state.stage !== 'success' && state.stage !== 'share') return state;
			return { ...state, stage: 'contacted' };

		default:
			return state;
	}
}
